import auth from '@react-native-firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
} from '@react-native-firebase/firestore';

export interface NotificationPayload {
  title: string;
  body: string;
  data?: {[key: string]: string};
}

export const CHANNEL_ID_CLOSED_PREFIX = 'road_closures_closed_';
export const CHANNEL_ID_OPENED_PREFIX = 'road_closures_opened_';
export const CHANNEL_ID_UPDATED_PREFIX = 'road_closures_updated_';

export const getClosedChannelId = (_soundFilename: string) => 'road_closures_closed_default';
export const getOpenedChannelId = (_soundFilename: string) => 'road_closures_opened_default';
export const getUpdatedChannelId = (_soundFilename: string) => 'road_closures_updated_system_default';

const getWebNotificationApi = () => (globalThis as any)?.Notification;

const getTokensFromUserData = (userData: any): string[] => {
  const tokens: string[] = [];
  const seen = new Set<string>();

  const tokenMap = userData?.fcmTokens;
  if (tokenMap && typeof tokenMap === 'object') {
    for (const token of Object.values(tokenMap)) {
      if (typeof token === 'string' && token && !seen.has(token)) {
        tokens.push(token);
        seen.add(token);
      }
    }
  }

  const legacyToken = userData?.fcmToken;
  if (typeof legacyToken === 'string' && legacyToken && !seen.has(legacyToken)) {
    tokens.push(legacyToken);
    seen.add(legacyToken);
  }

  return tokens;
};

const getPreferenceEnabled = (
  preferenceMap: {[key: string]: any} | undefined,
  accountId: string,
): boolean => {
  if (!preferenceMap || typeof preferenceMap !== 'object') {
    return true;
  }
  const value = preferenceMap[accountId];
  if (value === undefined || value === null) {
    return true;
  }
  return value === true || String(value).toLowerCase() === 'true';
};

const queueNotificationForFcm = async (
  accountId: string,
  payload: NotificationPayload,
): Promise<void> => {
  const db = getFirestore();
  const accountDoc = await getDoc(doc(db, 'accounts', accountId));
  if (!accountDoc.exists()) {
    return;
  }

  const accountData = accountDoc.data();
  const userIds: string[] = Array.isArray(accountData?.userIds) ? accountData.userIds : [];
  const fcmTokens: string[] = [];
  const tokenUserMap: {[token: string]: string} = {};
  const addUserTokens = (userDoc: any) => {
    if (!userDoc?.exists?.()) {
      return;
    }
    const userData = userDoc.data();
    const userId = userDoc.id;
    const tokens = getTokensFromUserData(userData);
    tokens.forEach((token) => {
      if (!fcmTokens.includes(token)) {
        fcmTokens.push(token);
        tokenUserMap[token] = userId;
      }
    });
  };

  // 1) Account users
  if (userIds.length > 0) {
    const userDocs = await Promise.all(userIds.map((userId) => getDoc(doc(db, 'users', userId))));
    userDocs.forEach((userDoc) => {
      if (!userDoc.exists()) return;
      const userData = userDoc.data();
      // Keep native behavior: off-duty municipality users do not get pushes.
      if (userData?.onDuty === false) {
        return;
      }
      addUserTokens(userDoc);
    });
  }

  // 2) Civilian followers
  try {
    const followersSnapshot = await getDocs(
      query(
        collection(db, 'users'),
        where('role', '==', 'civilian'),
        where('followedAccounts', 'array-contains', accountId),
      ),
    );
    followersSnapshot.forEach((followerDoc: any) => {
      if (!followerDoc.exists()) return;
      const followerData = followerDoc.data();
      const enabled = getPreferenceEnabled(
        followerData?.notificationPreferences || {},
        accountId,
      );
      if (!enabled) return;
      addUserTokens(followerDoc);
    });
  } catch (error) {
    // Don't fail queueing if follower lookup is denied/slow.
    console.warn('Web follower notification lookup failed:', error);
  }

  // 3) Mutual aid partner users
  try {
    const partnerAccountsSnapshot = await getDocs(
      query(
        collection(db, 'accounts'),
        where('mutualAidPartners', 'array-contains', accountId),
      ),
    );
    for (const partnerAccountDoc of partnerAccountsSnapshot.docs) {
      const partnerUserIds: string[] = Array.isArray(partnerAccountDoc.data()?.userIds)
        ? partnerAccountDoc.data().userIds
        : [];
      for (const partnerUserId of partnerUserIds) {
        if (userIds.includes(partnerUserId)) {
          continue;
        }
        const partnerUserDoc = await getDoc(doc(db, 'users', partnerUserId));
        if (!partnerUserDoc.exists()) continue;
        const partnerUserData = partnerUserDoc.data();
        if (partnerUserData?.onDuty === false) continue;
        const mutualAidPrefs = partnerUserData?.mutualAidNotificationPreferences || {};
        if (mutualAidPrefs[accountId] !== true) continue;
        addUserTokens(partnerUserDoc);
      }
    }
  } catch (error) {
    // Don't fail queueing if partner lookup fails.
    console.warn('Web mutual aid notification lookup failed:', error);
  }

  if (fcmTokens.length === 0) {
    return;
  }

  await addDoc(collection(db, 'notifications'), {
    accountId,
    payload,
    fcmTokens,
    tokenUserMap,
    createdAt: serverTimestamp(),
    status: 'pending',
    source: 'web',
    requestedBy: auth().currentUser?.uid || null,
  });
};

export const requestNotificationPermissions = async (): Promise<boolean> => {
  const NotificationApi = getWebNotificationApi();
  if (!NotificationApi) {
    return false;
  }

  if (NotificationApi.permission === 'granted') {
    return true;
  }

  if (NotificationApi.permission === 'denied') {
    return false;
  }

  try {
    const permission = await NotificationApi.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.warn('Web notification permission request failed:', error);
    return false;
  }
};

export const refreshFCMToken = async (): Promise<void> => {};

export const clearFCMToken = async (): Promise<void> => {};

export const sendNotificationsToAccount = async (
  accountId: string,
  payload: NotificationPayload,
  options?: {suppressLocalWebToast?: boolean},
): Promise<void> => {
  const NotificationApi = getWebNotificationApi();
  if (
    !options?.suppressLocalWebToast &&
    NotificationApi &&
    NotificationApi.permission === 'granted'
  ) {
    try {
      // Browser-level notification preview for web.
      const notification = new NotificationApi(payload.title, {
        body: payload.body,
        tag: payload.data?.closureId || undefined,
        renotify: false,
        data: payload.data || undefined,
      });

      notification.onclick = () => {
        try {
          (globalThis as any)?.focus?.();
        } catch {
          // Ignore focus issues.
        }
        notification.close();
      };
    } catch (error) {
      console.warn('Web notification display failed:', error);
    }
  }

  // Queue backend FCM delivery so other platforms (Android/iOS) receive
  // notifications created from the web app.
  try {
    await queueNotificationForFcm(accountId, payload);
  } catch (error) {
    console.warn('Web FCM queueing failed:', error);
  }
};

export const createOrUpdateNotificationChannel = async (
  _closedSoundFilename?: string,
  _openedSoundFilename?: string,
) => {};

export const initializeNotificationChannel = async () => {};

export const setupNotificationHandlers = () => {};
